import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/* ==========================================================================
 * W3 Family Final Verdict — unified prediction across W3, W3.1, W3.2, W3.3
 *
 * Each window emits a top-1 sign pattern (UP/DOWN/ZERO for B1-B5).
 * Per-ball voting → final 5-ball joint pattern.
 * Confidence = agreement level across the 4 windows.
 *
 * Renders identically on every page in the W3 family so the user always sees
 * the same master verdict no matter which sub-window they open.
 * ========================================================================== */

const W3_FAMILY = [
  { key: 'W3', name: 'W3 (Per-Ball Diff)', route: '/window3', color: 'from-blue-600 to-indigo-700', desc: 'Magnitude-aware per-ball difference scoring' },
  { key: 'W3.1', name: 'W3.1 (Pattern A-X)', route: '/window3-pattern', color: 'from-indigo-600 to-violet-700', desc: 'Sign pattern transition + gap + year diff' },
  { key: 'W3.2', name: 'W3.2 (7-Combo)', route: '/window3-2', color: 'from-violet-600 to-purple-700', desc: 'All 7 balls (B1-B5 + LS1+LS2)' },
  { key: 'W3.3', name: 'W3.3 (UP/DWN 5-Balls)', route: '/window3-3', color: 'from-purple-600 to-fuchsia-700', desc: '243-pattern frequency + transition' },
];

// Which walk-forward backtest signal backs each window's directional accuracy.
// W3/W3.1/W3.3 all derive their UP/DOWN/ZERO sign from the same 5-ball diff —
// only W3.2 adds LS1/LS2, so it alone uses the 7-position signal.
const BACKTEST_SOURCE = { 'W3': 'sign5', 'W3.1': 'sign5', 'W3.2': 'sign7', 'W3.3': 'sign5' };
const POSITION_LABELS_5 = ['B1', 'B2', 'B3', 'B4', 'B5'];
const POSITION_LABELS_7 = ['B1', 'B2', 'B3', 'B4', 'B5', 'LS1', 'LS2'];

// Convert any window's top-1 prediction into a 5-character sign string (U/D/Z)
function extractSignPattern(window, payload) {
  if (!payload) return null;
  try {
    if (window === 'W3' || window === 'W3.1') {
      // W3 & W3.1 share /analysis/per-ball-difference
      // sign_pattern_analysis.prediction.after_last_pattern[0].pattern → "+,-,+,+,-"
      const pat = payload.sign_pattern_analysis?.prediction?.after_last_pattern?.[0]?.pattern
                 || payload.sign_pattern_analysis?.top_patterns?.[0]?.pattern;
      if (!pat) return null;
      return pat.replace(/,/g, '').split('').map(s => s === '+' ? 'U' : s === '-' ? 'D' : 'Z').join('').slice(0, 5);
    }
    if (window === 'W3.2') {
      // /analysis/seven-combo: top combo prediction in prediction[0].pattern
      // e.g. "UP,UP,UP,UP,UP,DOWN,DOWN" (7 values: B1-B5 + LS1 + LS2)
      const top = payload.prediction?.[0] || payload.ranked_patterns?.[0];
      if (!top) return null;
      const combo = top.pattern || top.series || '';
      const parts = combo.split(',').slice(0, 5).map(s => s.trim());
      if (parts.length < 5) return null;
      return parts.map(p => p.toUpperCase().startsWith('U') ? 'U' : p.toUpperCase().startsWith('D') ? 'D' : 'Z').join('');
    }
    if (window === 'W3.3') {
      // /analysis/true-false-zero: top4_predictions[0].series  (e.g. "UP,DOWN,UP,UP,DOWN")
      const top = payload.top4_predictions?.[0] || payload.ranked_patterns?.[0];
      if (!top) return null;
      const series = top.series || top.pattern;
      if (!series) return null;
      const parts = series.split(',').map(s => s.trim());
      if (parts.length < 5) return null;
      return parts.slice(0, 5).map(p => p.toUpperCase().startsWith('U') ? 'U' : p.toUpperCase().startsWith('D') ? 'D' : 'Z').join('');
    }
  } catch {
    return null;
  }
  return null;
}

const SignChip = ({ char, large }) => {
  const cls = char === 'U' ? 'bg-emerald-500 text-white' : char === 'D' ? 'bg-rose-500 text-white' : 'bg-slate-300 text-slate-700';
  const sym = char === 'U' ? '\u25B2' : char === 'D' ? '\u25BC' : '=';
  return <span className={`inline-flex items-center justify-center rounded font-mono font-black ${cls} ${large ? 'w-9 h-9 text-lg' : 'w-6 h-6 text-[10px]'}`}>{sym}</span>;
};

// ---- Walk-forward directional-accuracy verdict (same convention as W50) ----
// gap = expected miss-streak length implied by the hit %; streak = current
// consecutive misses at the END of the walk-forward history.
function computeVerdict(streak, pct) {
  if (pct == null || pct <= 0) {
    return { tier: 'no_hits', gap: null, label: 'No hits yet', badge: 'bg-slate-700 text-white' };
  }
  const gap = Math.max(0, Math.round(100 / pct) - 1);
  if (streak === 0) return { tier: 'fresh', gap, label: 'Fresh hit', badge: 'bg-emerald-500 text-white' };
  if (streak < gap) return { tier: 'norm', gap, label: `Within norm`, badge: 'bg-slate-300 text-slate-800' };
  if (streak === gap) return { tier: 'due', gap, label: 'Due next', badge: 'bg-amber-400 text-amber-900' };
  if (streak === gap + 1) return { tier: 'over', gap, label: 'Overdue', badge: 'bg-rose-500 text-white' };
  return { tier: 'deep', gap, label: `Deep overdue (+${streak - gap})`, badge: 'bg-rose-700 text-white' };
}

// Compact per-position accuracy badge rendered under a window's sign chips.
const AccuracyBadge = ({ posStat, ballLabel }) => {
  if (!posStat) return null;
  const v = computeVerdict(posStat.current_miss_streak, posStat.pct);
  return (
    <span
      className={`inline-flex flex-col items-center justify-center rounded px-1 py-0.5 leading-none ${v.badge}`}
      title={`${ballLabel}: ${posStat.pct}% hit rate over ${posStat.tests} walk-forward draws · ${v.label}${v.gap != null ? ` (exp. gap ${v.gap})` : ''}`}
    >
      <span className="text-[9px] font-black">{posStat.pct}%</span>
    </span>
  );
};

export default function W3FamilyFinalVerdict({ activeWindow }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [backtest, setBacktest] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Note: W3 and W3.1 share the same endpoint; only fetch once
        const [perBall, sevenCombo, trueFalseZero, backtestRes] = await Promise.all([
          axios.get(`${API}/analysis/per-ball-difference`).then(r => r.data).catch(() => null),
          axios.get(`${API}/analysis/seven-combo`).then(r => r.data).catch(() => null),
          axios.get(`${API}/analysis/true-false-zero`).then(r => r.data).catch(() => null),
          axios.get(`${API}/analysis/w3-family-backtest`).then(r => r.data).catch(() => null),
        ]);
        if (cancelled) return;
        setData({ 'W3': perBall, 'W3.1': perBall, 'W3.2': sevenCombo, 'W3.3': trueFalseZero });
        setBacktest(backtestRes);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl bg-slate-100 border-2 border-slate-200 p-4 mb-4 animate-pulse" data-testid="w3-family-verdict-loading">
        <div className="h-4 w-48 bg-slate-300 rounded mb-2" />
        <div className="h-12 w-full bg-slate-200 rounded" />
      </div>
    );
  }

  // Build sub-verdicts per window
  const subVerdicts = W3_FAMILY.map(w => {
    const payload = data[w.key];
    const pattern = extractSignPattern(w.key, payload);
    return { ...w, pattern, hasPattern: !!pattern };
  });

  // Per-ball voting: count U/D/Z across the 4 windows for each position
  const ballVotes = [0, 1, 2, 3, 4].map(idx => {
    const votes = { U: 0, D: 0, Z: 0 };
    let total = 0;
    subVerdicts.forEach(sv => {
      if (sv.pattern && sv.pattern[idx]) {
        const ch = sv.pattern[idx];
        if (ch === 'U' || ch === 'D' || ch === 'Z') {
          votes[ch] += 1;
          total += 1;
        }
      }
    });
    // Pick winner
    let winner = null;
    let max = 0;
    Object.entries(votes).forEach(([ch, c]) => {
      if (c > max) { max = c; winner = ch; }
    });
    return { idx, votes, winner, max, total, agreement_pct: total ? Math.round((max / total) * 100) : 0 };
  });

  const finalPattern = ballVotes.map(v => v.winner || 'Z').join('');
  const overallAgreement = Math.round(ballVotes.reduce((sum, v) => sum + v.agreement_pct, 0) / 5);
  const respondingWindows = subVerdicts.filter(s => s.hasPattern).length;

  let confidenceLabel = 'LOW';
  let confColor = 'bg-slate-400 text-white';
  if (overallAgreement >= 75 && respondingWindows >= 3) { confidenceLabel = 'HIGH'; confColor = 'bg-emerald-500 text-white'; }
  else if (overallAgreement >= 50 && respondingWindows >= 2) { confidenceLabel = 'MEDIUM'; confColor = 'bg-blue-500 text-white'; }

  return (
    <div className="rounded-xl overflow-hidden border-2 border-violet-300 shadow-lg mb-5" data-testid="w3-family-verdict">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-700 via-purple-700 to-fuchsia-700 px-5 py-3 text-white">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-black text-lg flex items-center gap-2">
              <span className="text-xl">🎯</span> W3 Family — Master Verdict
            </h3>
            <p className="text-[11px] text-white/85 mt-0.5">
              Aggregates UP/DOWN/ZERO sign predictions across W3, W3.1, W3.2, W3.3 · {respondingWindows}/4 windows responding · {overallAgreement}% per-ball agreement
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full ${confColor}`} data-testid="w3-family-confidence">
              {confidenceLabel}
            </span>
            <button onClick={() => setCollapsed(!collapsed)} className="text-[10px] uppercase font-bold bg-white/20 hover:bg-white/30 rounded px-2 py-1 transition" data-testid="w3-family-toggle">
              {collapsed ? '▼ Expand' : '▲ Collapse'}
            </button>
          </div>
        </div>
      </div>

      {!collapsed && (
        <div className="bg-white p-4 space-y-4">
          {/* Final pattern */}
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-lg p-4" data-testid="w3-family-final">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-wider font-black text-amber-800">⭐ Combined Pick — Per-Ball Majority Vote</div>
              <span className="text-[10px] text-amber-700">Pattern: <b className="font-mono text-base">{finalPattern}</b></span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {ballVotes.map(v => (
                <div key={v.idx} className="bg-white rounded p-2 text-center border" data-testid={`w3-family-ball-${v.idx + 1}`}>
                  <div className="text-[9px] uppercase font-bold text-slate-500 mb-1">B{v.idx + 1}</div>
                  <div className="flex justify-center mb-1">
                    <SignChip char={v.winner || 'Z'} large />
                  </div>
                  <div className="text-[9px] text-slate-500 font-mono">
                    {v.max}/{v.total} agree
                  </div>
                  <div className="text-[9px] text-slate-400">
                    U:{v.votes.U} D:{v.votes.D} Z:{v.votes.Z}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Per-window sub-verdicts */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-black text-slate-600 mb-2">📋 Each Window's Top-1 Verdict</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {subVerdicts.map(sv => {
                const isActive = activeWindow === sv.key;
                return (
                  <button
                    key={sv.key}
                    onClick={() => navigate(sv.route)}
                    className={`text-left rounded-lg p-3 border-2 transition ${isActive ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-200' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}`}
                    data-testid={`w3-family-sub-${sv.key.replace('.', '_')}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[10px] uppercase font-black px-2 py-0.5 rounded text-white bg-gradient-to-r ${sv.color}`}>{sv.key}</span>
                      <span className="text-[9px] text-slate-500">{sv.name}</span>
                    </div>
                    <p className="text-[9px] text-slate-500 mb-2 italic">{sv.desc}</p>
                    {sv.hasPattern ? (
                      <>
                        <div className="flex items-center gap-1">
                          {sv.pattern.split('').map((ch, i) => <SignChip key={i} char={ch} />)}
                          <span className="ml-auto text-[9px] text-slate-400">→ open ↗</span>
                        </div>
                        {(() => {
                          const srcKey = BACKTEST_SOURCE[sv.key];
                          const posStats = backtest?.[srcKey];
                          if (!posStats || posStats.length === 0) return null;
                          const labels = srcKey === 'sign7' ? POSITION_LABELS_7 : POSITION_LABELS_5;
                          const sharesSignal = sv.key === 'W3.1' || sv.key === 'W3.3';
                          return (
                            <div className="mt-1.5 pt-1.5 border-t border-slate-200">
                              <div className="flex items-center gap-1 flex-wrap" data-testid={`w3-family-accuracy-${sv.key.replace('.', '_')}`}>
                                <span className="text-[8px] uppercase font-bold text-slate-400 mr-0.5">Walk-fwd hit%:</span>
                                {posStats.map((ps, i) => (
                                  <AccuracyBadge key={i} posStat={ps} ballLabel={labels[i] || `#${i + 1}`} />
                                ))}
                              </div>
                              {sharesSignal && (
                                <p className="text-[8px] text-slate-400 italic mt-0.5">Shares core UP/DOWN/ZERO signal with W3</p>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <div className="text-[10px] text-slate-400 italic">No prediction available yet</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reasoning */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-slate-700 leading-relaxed" data-testid="w3-family-reasoning">
            <b className="text-slate-900">Verdict:</b> Across {respondingWindows} of 4 W3-family windows, the per-ball majority vote produces pattern <b className="font-mono">{finalPattern}</b> with <b>{overallAgreement}%</b> average agreement.
            {' '}{confidenceLabel === 'HIGH' && <span className="text-emerald-700 font-bold">All windows align strongly — high-conviction signal.</span>}
            {confidenceLabel === 'MEDIUM' && <span className="text-blue-700 font-bold">Most windows agree — solid signal but watch the dissenting balls.</span>}
            {confidenceLabel === 'LOW' && <span className="text-amber-700 font-bold">Windows disagree — wait for more agreement before betting confidently.</span>}
          </div>
          {backtest?.methodology && (
            <p className="text-[9px] text-slate-400 italic leading-relaxed" data-testid="w3-family-backtest-methodology">
              Walk-fwd hit% methodology: {backtest.methodology} Tested across {backtest.sign5?.[0]?.tests || 0} historical draws (min. {backtest.min_train} draws training before scoring begins).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
